// packages/common/src/sse/useAiReviewSSE.ts
import { useCallback, useRef } from 'react';
import { useSSE } from './useSSE';

export type AiReviewEventName =
  | 'ai_script_review_ready'
  | 'ai_content_review_ready'
  | 'ai_geo_ready'
  // Fires for BOTH the success and terminal-failure branches of the GEO
  // finalize pipeline — there is no separate "failed" event on the wire;
  // the payload's `status: 'failed'` field (present only on failure)
  // differentiates them (see discovr's internal/sse/event.go doc comment
  // on EventAIGeoFinalizeReady). A refetch handles either case identically.
  | 'ai_geo_finalize_ready'
  | 'ai_brand_safety_review_ready'
  | 'ai_sentiment_review_ready'
  | 'ai_audience_dynamics_campaign_ready'
  | 'ig_profile_updated';

interface Options<TPayload extends Record<string, unknown>> {
  eventName: AiReviewEventName;
  enabled: boolean;
  filter?: (payload: TPayload) => boolean;
  onReady: (payload: TPayload) => void;
}

export function useAiReviewSSE<TPayload extends Record<string, unknown>>({
  eventName,
  enabled,
  filter,
  onReady,
}: Options<TPayload>): void {
  // Keep refs current on every render so the stable handler always calls
  // the latest filter/onReady without triggering re-subscription.
  const filterRef = useRef(filter);
  filterRef.current = filter;
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  const handler = useCallback((payload: TPayload) => {
    if (filterRef.current && !filterRef.current(payload)) return;
    onReadyRef.current(payload);
  }, []); // stable — always calls latest via refs

  useSSE(eventName, enabled, handler);
}
